import {
  CheckingAccountDto,
  PurchaseProvider,
  PurchaseState,
} from "@kvix/shared";
import { Button, KvixIcon, Text } from "@kvix/ui";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Dialog,
  DialogActions,
  DialogContent,
  Link,
  makeStyles,
} from "@material-ui/core";
import { TFunction } from "i18next";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  isBasicInstructor,
  isInstructor,
} from "../../../../../../contexts/user";
import { CoinImageSize } from "../../../../Payment/BuyCoins/enums/CoinImageSize";
import { CoinImage } from "../../../../Payment/BuyCoins/partials/CoinImage";

const useStyles = makeStyles((theme) => ({
  dialogContainer: {
    textAlign: "center",
    padding: "38px",
    margin: "auto",
    width: "100%",
  },
  dialogTitle: {
    whiteSpace: "pre-line",
    letterSpacing: "0",
    lineHeight: "44px",
  },
  dialogContentNoPadding: {
    "&&.MuiDialogContent-root": {
      padding: 0,
    },
    margin: "12px 0",
  },
  checkingAccountAccordian: {
    "&.Mui-expanded": {
      margin: 0,
    },
    "&.MuiAccordionSummary-content.Mui-expanded": {
      margin: 0,
    },
    "& .MuiAccordionSummary-root": {
      padding: 0,
      "&.Mui-expanded": {
        minHeight: 0,
      },
    },
    "& .MuiAccordionDetails-root": {
      padding: "0 0 16px 0",
    },
    "& .MuiAccordionSummary-content.Mui-expanded": {
      margin: "12px 0",
    },
  },
  accordianIcon: {
    marginRight: "8px",
  },
  koin: {
    marginLeft: "3px",
    marginBottom: "-2px",
  },
}));

const purchaseProviderToString = (
  provider: PurchaseProvider,
  t: TFunction
): string => {
  switch (provider) {
    case PurchaseProvider.APPLE:
      return t("account.accountDetails.purchaseProviders.apple");
    case PurchaseProvider.GOOGLE:
      return t("account.accountDetails.purchaseProviders.google");
    case PurchaseProvider.KVIX:
      return t("account.accountDetails.purchaseProviders.kvix");
    case PurchaseProvider.STRIPE:
      return t("account.accountDetails.purchaseProviders.stripe");
    case PurchaseProvider.SWISH:
      return t("account.accountDetails.purchaseProviders.swish");
    default:
      return "N/A";
  }
};

const CheckingAccountAccordian: React.FC<{
  checkingAccount: CheckingAccountDto;
  handleChange: (
    panel: string
  ) => (event: React.ChangeEvent<{}>, isExpanded: boolean) => void;
  expanded: string;
}> = (props) => {
  const classes = useStyles(props);
  const { checkingAccount, handleChange, expanded } = props;
  const { t, ready } = useTranslation(["account", "general"]);

  if (!ready) {
    return null;
  }

  return (
    <Accordion
      expanded={expanded === checkingAccount.id.toString()}
      onChange={handleChange(checkingAccount.id.toString())}
      key={checkingAccount.id}
      className={classes.checkingAccountAccordian}
    >
      <AccordionSummary>
        {expanded === checkingAccount.id.toString() ? (
          <KvixIcon.Expand.ExpandLess className={classes.accordianIcon} />
        ) : (
          <KvixIcon.Expand.ExpandMore className={classes.accordianIcon} />
        )}
        {new Date(checkingAccount.created.toString()).toLocaleString("sv-SE", {
          day: "numeric",
          month: "numeric",
          year: "numeric",
        })}
        {
          <span style={{ marginLeft: "auto" }}>
            {checkingAccount.amount}
            <CoinImage className={classes.koin} size={CoinImageSize.MD} />
          </span>
        }
      </AccordionSummary>
      <AccordionDetails>
        {checkingAccount.toUser ? (
          <span>
            {`${t("account.accountDetails.recipient")}: `}
            <Link
              href={
                (isInstructor(checkingAccount.toUser) &&
                  "/instructor/" + checkingAccount.toUser.id) ||
                (isBasicInstructor(checkingAccount.toUser) &&
                  "/streamer/" + checkingAccount.toUser.id)
              }
            >
              {checkingAccount.toUser.name?.custom ||
                `${checkingAccount.toUser.name?.first} ${checkingAccount.toUser.name?.last}`}
            </Link>
          </span>
        ) : (
          <span>{`${t(
            "account.accountDetails.purchaseProvider"
          )}: ${purchaseProviderToString(checkingAccount.provider, t)}`}</span>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export const CheckingAccountsDialog: React.FC<{
  open: boolean;
  handleOpen: (value: boolean) => void;
  checkingAccountList: CheckingAccountDto[];
}> = (props) => {
  const classes = useStyles(props);
  const { t, ready } = useTranslation(["account", "general"]);
  const [expanded, setExpanded] = useState<string>(null);
  const checkingAccountList = props.checkingAccountList.filter(
    (ca) => ca.state === PurchaseState.FINALIZED
  );
  const [pagination, setPagination] = useState<number>(10);

  const handleChange =
    (panel: string) => (event: React.ChangeEvent<{}>, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : null);
    };

  if (!ready) {
    return null;
  }

  return (
    <Dialog open={props.open} onClose={() => props.handleOpen(false)}>
      <div className={classes.dialogContainer}>
        <Text className={classes.dialogTitle} variant={"h3"}>
          {t("account.accountDetails.kvixCoins") +
            " " +
            t("account.accountDetails.kvixCoinsHistory")}
        </Text>
        <DialogContent className={classes.dialogContentNoPadding}>
          <div>
            {checkingAccountList.map((ca: CheckingAccountDto, index) => {
              if (index < pagination)
                return (
                  <CheckingAccountAccordian
                    checkingAccount={ca}
                    expanded={expanded}
                    handleChange={handleChange}
                  />
                );
            })}
          </div>
          {checkingAccountList.length >= pagination && (
            <Button
              variant="text"
              onClick={() => setPagination(pagination + 10)}
            >
              {t("general:showMore")}
            </Button>
          )}
        </DialogContent>
      </div>
      <DialogActions>
        <Button onClick={() => props.handleOpen(false)}>
          {t("general:buttons.close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
